import { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Loader2, FileText, CheckCircle2, XCircle, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { PageHeader, PageState, StatusPill, Empty } from "@/components/app/Common";
import { DocViewer } from "@/components/app/DocViewer";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const DOCS = [["revival_cover_letter", "Revival Cover Letter"], ["filing_checklist", "Filing Checklist"], ["revival_package", "Revival Package"],
  ["recovery_report", "Compliance Recovery Report"], ["action_plan", "Action Plan"]];

const RevivalCard = ({ item, reload, onDoc }) => {
  const { entity: e, eligibility: el, revival: r, progress } = item;
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(null);
  const start = async () => {
    await api.post(`/revival/${e.id}/start`, { target_date: target || null });
    toast.success("Revival workflow started");
    reload();
  };
  const toggle = async (key, done) => {
    await api.put(`/revival/${e.id}/checklist`, { key, done });
    reload();
  };
  const gen = async (kind) => {
    setBusy(kind);
    try {
      onDoc((await api.post("/ai/generate", { kind, entity_id: e.id })).data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(null);
    }
  };
  const finish = async () => {
    await api.post(`/revival/${e.id}/complete`);
    toast.success(`${e.name} restored to Active`);
    reload();
  };
  return (
    <div className="surface p-6" data-testid={`revival-card-${e.id}`}>
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div>
          <div className="flex items-center gap-2"><h3 className="text-lg font-semibold">{e.name}</h3><StatusPill status={e.status} /></div>
          <div className="text-sm text-muted-foreground mt-1">{e.jurisdiction} · dissolved {fmtDate(e.dissolution_date)}</div>
        </div>
        <div className={`rounded-lg border px-4 py-3 text-sm max-w-md ${el.eligible ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid={`revival-eligibility-${e.id}`}>
          <div className="flex items-center gap-2 font-semibold">{el.eligible ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
            {el.eligible ? "Eligible for revival" : "Outside revival window"}</div>
          <div className="text-xs text-muted-foreground mt-1">{el.form} · {el.authority}</div>
          <div className="text-xs text-muted-foreground mt-1">{el.note}</div>
        </div>
      </div>
      {!r ? (
        <div className="mt-6 flex flex-wrap items-end gap-3">
          <label className="text-xs text-muted-foreground space-y-1">Target revival date<Input type="date" value={target} onChange={(x) => setTarget(x.target.value)} data-testid={`revival-target-${e.id}`} /></label>
          <Button onClick={start} data-testid={`revival-start-${e.id}`}>Start revival workflow</Button>
        </div>
      ) : (
        <div className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div>
            <div className="flex justify-between text-sm mb-2"><span className="font-medium">Revival progress</span><span className="font-mono" data-testid={`revival-progress-${e.id}`}>{progress}%</span></div>
            <Progress value={progress} className="h-2" />
            <ul className="mt-4 space-y-2">
              {r.checklist.map((c) => (
                <li key={c.key} className="flex items-start gap-3 text-sm">
                  <Checkbox checked={c.done} onCheckedChange={(v) => toggle(c.key, !!v)} className="mt-0.5" data-testid={`revival-check-${e.id}-${c.key}`} />
                  <span className={c.done ? "line-through text-muted-foreground" : ""}>{c.label}</span>
                </li>
              ))}
            </ul>
            {progress === 100 && <Button className="mt-5 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={finish} data-testid={`revival-complete-${e.id}`}><PartyPopper className="h-4 w-4 mr-1" />Mark entity revived</Button>}
          </div>
          <div>
            <div className="text-sm font-medium mb-3">Document generator</div>
            <div className="space-y-2">
              {DOCS.map(([k, l]) => (
                <Button key={k} variant="outline" className="w-full justify-start" onClick={() => gen(k)} disabled={!!busy} data-testid={`revival-gen-${k}-${e.id}`}>
                  {busy === k ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}{l}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">Drafts are AI-generated from your records. Review before submitting to a registry.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Revival() {
  const state = useLoad(() => api.get("/revival").then((r) => r.data));
  const [doc, setDoc] = useState(null);
  return (
    <div>
      <PageHeader eyebrow="Module 4" title="Revival workflow" subtitle="Bring dissolved entities back into good standing — eligibility, checklist, progress and a full revival package." />
      <PageState state={state}>
        {state.data?.length === 0 ? (
          <Empty icon={LifeBuoy} title="No dissolved entities" text="When an entity's status is set to Dissolved it appears here with a jurisdiction-specific revival plan." testId="revival-empty" />
        ) : (
          <div className="space-y-6">{state.data?.map((it) => <RevivalCard key={it.entity.id} item={it} reload={state.reload} onDoc={setDoc} />)}</div>
        )}
      </PageState>
      <DocViewer doc={doc} onClose={() => setDoc(null)} />
    </div>
  );
}
